<template>
  <v-app>
    <span v-if="loggedIn">
      <v-navigation-drawer
        floating
        v-model="drawer"
        :rail="mini && !mobile"
        :expand-on-hover="mini && !mobile"
        :rail-width="56"
        :color="drawerColor"
        class="text-white"
        @mouseenter="isHovered = true"
        @mouseleave="isHovered = false"
      >
        <v-list density="compact" nav class="pt-3 py-0 text-white">
          <v-menu v-if="mobile">
            <template v-slot:activator="{ props }">
              <v-list-item v-bind="props">
                <v-list-item-title class="text-subtitle-1 text-center">
                  {{ company?.description }}
                </v-list-item-title>

                <template v-slot:append>
                  <v-icon :icon="mdiMenuDown" />
                </template>
              </v-list-item>
            </template>

            <v-list>
              <v-list-item>
                <v-list-item-title class="text-truncate">
                  {{ email }}
                </v-list-item-title>
              </v-list-item>

              <v-divider></v-divider>
              <v-list-item :to="{ name: 'profile' }">
                <v-list-item-title>
                  <v-icon :icon="mdiAccount" />
                  Perfil
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="onLogout">
                <v-list-item-title>
                  <v-icon :icon="mdiLogout" />
                  Sair
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-list-item v-for="(item, i) in visibleItems" :key="i" :to="item.to">
            <template v-slot:prepend>
              <v-icon size="small" class="icon-drawer" :icon="item.icon" />
            </template>

            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
        </v-list>

        <template v-slot:prepend>
          <div
            class="drawer-logo-header d-flex flex-column align-center justify-center"
            :class="showRailLogo ? 'pa-2' : 'pa-4'"
          >
            <img
              :src="elginLogo"
              alt="Logo"
              class="drawer-logo"
              :class="{ 'drawer-logo-rail': showRailLogo }"
            />
            <span v-if="!showRailLogo" class="vendor-badge mt-1"> VENDOR </span>
          </div>
        </template>
      </v-navigation-drawer>

      <v-app-bar elevation="0" :color="drawerColor" class="text-white">
        <v-app-bar-nav-icon v-if="mobile" @click="toggleDrawer" />

        <div v-if="!mobile" class="d-flex flex-column justify-center">
          <app-breadcrumbs class="app-bar-breadcrumbs" />
        </div>
        <v-toolbar-title v-else>
          <div class="text-h6">
            <h3 class="toolbar-title">{{ toolbarTitle }}</h3>
          </div>
        </v-toolbar-title>

        <v-spacer />

        <v-btn icon variant="text" @click="toggleTheme">
          <v-icon :icon="isDark ? mdiWeatherSunny : mdiWeatherNight" />
        </v-btn>

        <v-menu v-if="!mobile">
          <template v-slot:activator="{ props }">
            <v-btn
              variant="text"
              v-bind="props"
              class="text-none font-weight-light"
            >
              {{ email }}
              <v-icon :icon="mdiChevronDown" />
            </v-btn>
          </template>

          <v-list>
            <v-list-item>
              <v-list-item-title class="text-truncate">
                {{ email }}
              </v-list-item-title>
            </v-list-item>

            <v-divider></v-divider>
            <v-list-item :to="{ name: 'profile' }">
              <v-list-item-title>
                <v-icon :icon="mdiAccount" />
                Perfil
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="toggleTheme">
              <v-list-item-title>
                <v-icon :icon="isDark ? mdiWeatherSunny : mdiWeatherNight" />
                {{ isDark ? "Modo Claro" : "Modo Escuro" }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="onLogout">
              <v-list-item-title>
                <v-icon :icon="mdiLogout" />
                Sair
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-app-bar>
    </span>

    <v-main>
      <toolbar-loading />

      <router-view />
    </v-main>

    <snackbar-component />
  </v-app>
</template>

<script setup>
import { ref, computed, watch, inject, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useDisplay } from "vuetify";
import { useTheme } from "vuetify";
import {
  mdiMenuDown,
  mdiAccount,
  mdiLogout,
  mdiWeatherSunny,
  mdiWeatherNight,
  mdiChevronDown,
  mdiViewDashboard,
  mdiCardAccountDetails,
  mdiAccountMultiple,
  mdiPackageVariant,
} from "@mdi/js";
import { useAuthStore } from "@/stores/auth";
import { usePrefsStore } from "@/stores/prefs";
import SnackbarComponent from "@/plugins/snackbar/SnackbarComponent.vue";
import ToolbarLoading from "@/plugins/toolbar-loading/ToolbarLoadingComponent.vue";
import AppBreadcrumbs from "@/components/AppBreadcrumbs.vue";
import { acl } from "@/acl";

import elginLight from "@/assets/elgin-light.svg";
import elginRailLight from "@/assets/elgin-rail-light.svg";

const $can = inject("$can");

const router = useRouter();
const route = useRoute();
const { mobile } = useDisplay();
const authStore = useAuthStore();

const theme = useTheme();
const prefsStore = usePrefsStore();

const drawer = ref(true);
const mini = ref(prefsStore.drawerRail);
const toolbarTitle = ref("");

const isHovered = ref(false);

const isDark = computed(() => theme.global.current.value.dark);

onMounted(() => {
  theme.change(prefsStore.theme);
});

const toggleTheme = () => {
  const newTheme = isDark.value ? "light" : "dark";
  theme.change(newTheme);
  prefsStore.setTheme(newTheme);
};

const drawerColor = "#16213f";

const showRailLogo = computed(
  () => mini.value && !mobile.value && !isHovered.value,
);

const elginLogo = computed(() => {
  return showRailLogo.value ? elginRailLight : elginLight;
});

const items = [
  {
    icon: mdiViewDashboard,
    title: "Dashboard",
    active: true,
    to: { name: "home" },
  },
  {
    active: true,
    title: "Clientes",
    icon: mdiCardAccountDetails,
    to: { name: "tenant.index" },
    permission: "company:read",
  },
  {
    active: true,
    title: "Acessos (vendor)",
    icon: mdiAccountMultiple,
    to: { name: "grants.index" },
    permission: "grant:read",
  },
  {
    active: true,
    title: "Software",
    icon: mdiPackageVariant,
    to: { name: "software.index" },
    scope: "ops:software:upload",
  },
];

const loggedIn = computed(() => authStore.isLoggedIn);
const company = computed(() => authStore.currentTenant);
const email = computed(() => authStore.user?.email);
const isDesktop = computed(() => !mobile.value);

const visibleItems = computed(() => {
  return items.filter((item) => {
    if (item.permission && !$can(item.permission)) {
      return false;
    }
    if (item.scope && !acl.hasScope(authStore.token, item.scope)) {
      return false;
    }
    return true;
  });
});

const onLogout = () => {
  authStore.logout();
  router.replace({ name: "login" });
};

const toggleDrawer = () => {
  drawer.value = !drawer.value;
};

// Watch route changes for toolbar title
watch(
  route,
  () => {
    toolbarTitle.value = route.meta?.title || "";
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.drawer-logo-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 8px;
  min-height: 64px;
}

.drawer-logo {
  max-width: 120px;
  width: 100%;
  height: auto;
}

.drawer-logo-rail {
  max-width: 28px;
}

.vendor-badge {
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  color: rgba(255, 255, 255, 0.5);
}

.app-bar-breadcrumbs {
  padding: 0;
}
</style>
